import { useEffect, useRef, useState } from "react";

// UX-6a A1: shimmer is a courtesy, not a requirement. When the viewer prefers
// reduced motion we drop the pulse and show a static tint — the "free 6e credit".
export function usePrefersReducedMotion() {
  const query = "(prefers-reduced-motion: reduce)";
  const [reduced, setReduced] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia(query);
    const handler = (event) => setReduced(event.matches);
    // addEventListener is the modern API; some engines still expose addListener.
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else if (mq.addListener) mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else if (mq.removeListener) mq.removeListener(handler);
    };
  }, []);

  return reduced;
}

// UX-6a A1: once a skeleton is shown it stays up for at least `minMs`, so a fast
// query can't cause a one-frame flash. `active` is the raw loading flag; the
// returned value is the debounced "should the skeleton be on screen" flag. Timer-
// only (no wall-clock reads) so it behaves deterministically under fake timers.
export function useMinimumVisible(active, minMs = 200) {
  const [visible, setVisible] = useState(Boolean(active));
  const [canHide, setCanHide] = useState(!active);
  const prevActive = useRef(active);

  // On the rising edge (skeleton appears), show it and re-arm the min-visible gate.
  useEffect(() => {
    if (active) {
      setVisible(true);
      if (!prevActive.current) setCanHide(false);
    }
    prevActive.current = active;
  }, [active]);

  // The min-visible timer is keyed on `canHide` (not `active`), so it is NOT
  // cancelled when loading resolves — it always runs its full minMs.
  useEffect(() => {
    if (canHide) return undefined;
    const timer = setTimeout(() => setCanHide(true), minMs);
    return () => clearTimeout(timer);
  }, [canHide, minMs]);

  // Hide only once loading is done AND the minimum has elapsed.
  useEffect(() => {
    if (!active && canHide) setVisible(false);
  }, [active, canHide]);

  return visible;
}
